import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Modal, Select, InputNumber, Button } from 'antd';
import { ShoppingOutlined } from '@ant-design/icons';
import { get, post } from '../../../../services/ApiService';

const THEME = {
  primary: '#4e7661',
  light: '#eef4f1',
  border: '#c5d9ce',
  muted: '#3d5f4e',
};

const fieldLabel = {
  fontSize: 11,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.7px',
  color: '#aaa',
  margin: '0 0 6px',
};

const AlternativeStockModal = ({ open, onCancel, onSuccess, order }) => {
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [catalogError, setCatalogError] = useState('');
  const [ppid, setPpid] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setProducts([]);
    setCatalogError('');
    setPpid(null);
    setQuantity(1);
    setResult(null);

    const loadProducts = async () => {
      setProductsLoading(true);
      try {
        const response = await get('api/products/ppid');
        if (cancelled) return;
        if (response?.success && Array.isArray(response.products)) {
          setProducts(response.products);
        } else if (!cancelled) {
          setCatalogError('The product list could not be loaded. Close this and try again.');
        }
      } catch (error) {
        if (!cancelled && !error.handledByInterceptor) {
          setCatalogError('The product list could not be loaded. Close this and try again.');
        }
      } finally {
        if (!cancelled) setProductsLoading(false);
      }
    };

    loadProducts();
    return () => {
      cancelled = true;
    };
  }, [open]);

  const selected = products.find((product) => product.id === ppid) || null;
  const sku = selected?.skus?.[0] || '';
  const succeeded = result?.success === true;
  const canSubmit = Boolean(sku) && Number.isInteger(quantity) && quantity >= 1 && !submitting && !succeeded;
  const orderProducts = order?.products || [];
  const willReturnExistingStock = order?.status === 'SHIPPED' && (order?.invoices || []).some(
    (group) => (group.invoiceRecord || []).length > 0
  );

  const handleClose = () => {
    if (submitting) return;
    onCancel();
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setResult(null);
    try {
      const response = await post(`api/orders/${order._id}/alternative-stock`, {
        ppid,
        sku,
        quantity,
      });
      if (response?.success) {
        setResult({
          success: true,
          message: response.restoredPrevious
            ? `Existing stock was returned, then ${response.sku || sku} was deducted.`
            : `Stock deducted from ${response.sku || sku}. This order will not be deducted again at dispatch.`,
        });
        onSuccess(response.invoices);
      } else {
        setResult({
          success: false,
          message: response?.message || 'Stock could not be deducted.',
        });
      }
    } catch (error) {
      if (!error.handledByInterceptor) {
        setResult({
          success: false,
          message: error.response?.data?.message || 'Stock could not be deducted.',
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span
            style={{
              width: 32,
              height: 32,
              borderRadius: 8,
              background: THEME.light,
              border: `1px solid ${THEME.border}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <ShoppingOutlined style={{ color: THEME.primary, fontSize: 16 }} />
          </span>
          <div>
            <div style={{ fontWeight: 700, fontSize: 15, lineHeight: 1.3 }}>
              Use alternative stock
            </div>
            <div style={{ fontSize: 11, color: '#999', fontWeight: 400 }}>
              {order?.orderId ? `Order ${order.orderId}` : 'Deduct stock for this order'}
            </div>
          </div>
        </div>
      }
      open={open}
      onCancel={handleClose}
      footer={null}
      width={480}
      maskClosable={!submitting}
      closable={!submitting}
      destroyOnClose
    >
      <div style={{ padding: '4px 0 8px' }}>
        <p style={{ fontSize: 13, color: '#666', lineHeight: 1.55, margin: '0 0 16px' }}>
          {willReturnExistingStock
            ? 'This shipped order already has stock deducted. That stock is returned first, then the alternative product is deducted and its invoice is stored on the order.'
            : 'Deduct inventory for this order from a different product. The first SKU of the product you choose is used, and the invoice is stored on the order.'}
        </p>

        {orderProducts.length > 0 && (
          <div
            style={{
              border: '1px solid #eee',
              borderRadius: 10,
              padding: '12px 14px',
              background: '#fafafa',
              marginBottom: 16,
            }}
          >
            <p style={fieldLabel}>Ordered products</p>
            {orderProducts.map((item) => (
              <div
                key={`${item.sku}-${item.title}`}
                style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginTop: 6 }}
              >
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 13, color: '#333', lineHeight: 1.4 }}>
                    {item.title || item.sku}
                  </div>
                  {item.sku && (
                    <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>{item.sku}</div>
                  )}
                </div>
                <div style={{ fontSize: 13, color: '#666', flexShrink: 0 }}>×{item.quantity}</div>
              </div>
            ))}
          </div>
        )}

        <div style={{ marginBottom: 16 }}>
          <p style={fieldLabel}>Alternative product</p>
          <Select
            showSearch
            size="large"
            optionFilterProp="label"
            placeholder={productsLoading ? 'Loading products…' : 'Search for a product'}
            loading={productsLoading}
            disabled={productsLoading || succeeded || Boolean(catalogError)}
            value={ppid ?? undefined}
            onChange={setPpid}
            style={{ width: '100%' }}
            options={products.map((product) => ({
              value: product.id,
              label: `pp${product.id} - ${product.product}`,
            }))}
          />
        </div>

        {selected && !sku && (
          <div
            style={{
              borderRadius: 10,
              padding: '12px 14px',
              marginBottom: 16,
              background: '#fff2f0',
              border: '1px solid #ffccc7',
              color: '#cf1322',
              fontSize: 13,
              lineHeight: 1.5,
            }}
          >
            This product has no SKU, so stock cannot be deducted from it.
          </div>
        )}

        {sku && (
          <div
            style={{
              border: `2px solid ${THEME.primary}`,
              borderRadius: 10,
              padding: '12px 14px',
              background: THEME.light,
              marginBottom: 16,
            }}
          >
            <p style={{ ...fieldLabel, color: THEME.primary }}>Stock will be taken from</p>
            <div style={{ fontWeight: 600, fontSize: 13, color: THEME.primary, lineHeight: 1.4 }}>
              {selected.product}
            </div>
            <div style={{ fontSize: 12, color: THEME.muted, marginTop: 4 }}>
              First SKU · {sku}
            </div>
          </div>
        )}

        <div style={{ marginBottom: 16 }}>
          <p style={fieldLabel}>Quantity</p>
          <InputNumber
            min={1}
            precision={0}
            size="large"
            disabled={succeeded}
            value={quantity}
            onChange={(value) => setQuantity(value)}
            style={{ width: '100%' }}
          />
        </div>

        {(catalogError || result) && (
          <div
            style={{
              borderRadius: 10,
              padding: '12px 14px',
              marginBottom: 16,
              background: succeeded ? '#f6ffed' : '#fff2f0',
              border: `1px solid ${succeeded ? '#b7eb8f' : '#ffccc7'}`,
              color: succeeded ? '#389e0d' : '#cf1322',
              fontSize: 13,
              lineHeight: 1.5,
            }}
          >
            {succeeded ? result.message : (result?.message || catalogError)}
          </div>
        )}

        {succeeded ? (
          <Button
            type="primary"
            block
            size="large"
            onClick={handleClose}
            style={{
              background: THEME.primary,
              borderColor: THEME.primary,
              fontWeight: 600,
              fontSize: 14,
              height: 44,
            }}
          >
            Done
          </Button>
        ) : (
          <Button
            type="primary"
            block
            size="large"
            icon={<ShoppingOutlined />}
            loading={submitting}
            disabled={!canSubmit}
            onClick={handleSubmit}
            style={{
              background: canSubmit ? THEME.primary : undefined,
              borderColor: canSubmit ? THEME.primary : undefined,
              fontWeight: 600,
              fontSize: 14,
              height: 44,
            }}
          >
            {willReturnExistingStock ? 'Return stock and deduct' : 'Deduct stock for this order'}
          </Button>
        )}
      </div>
    </Modal>
  );
};

AlternativeStockModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
  order: PropTypes.shape({
    _id: PropTypes.string,
    orderId: PropTypes.string,
    products: PropTypes.arrayOf(PropTypes.shape({
      title: PropTypes.string,
      sku: PropTypes.string,
      quantity: PropTypes.number,
    })),
  }).isRequired,
};

export default AlternativeStockModal;
